import React, { useEffect, useState } from 'react';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Card } from '../../../../../../CommonComponents/OPL/Card/Card';
import { ColumnChart } from '../../../../../../CommonComponents/OPL/charts/ColumnChart/ColumnChart';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { PDFTitle } from '../../../../../../PDFRenderer/components/PDFTitle/PDFTitle';
import { useChartReflow } from '../../../../hooks/useChartReflow';
import { Breakdown } from './components/Breakdown/Breakdown';
import { useRetirementIncome } from './useRetirementIncome';

interface RetirementIncomeProps {
  goalId?: string;
  title?: string;
}

export const RetirementIncome: React.FC<RetirementIncomeProps> = ({ goalId, title }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const onChartClick = (index: number) => {
    setSelectedIndex(index);
  };

  const { data } = useRetirementIncome({ onChartClick, goalId });

  const { chartRef } = useChartReflow();

  useEffect(() => {
    // Call reflow to change the chart dimensions when opening/closing the Breakdown
    if (chartRef && chartRef.current) {
      chartRef.current.chart.reflow();
    }
  }, [selectedIndex]);

  if (!data) {
    return null;
  }

  const { categories, series, values, customSettings, isPdf } = data;

  return (
    <Box position="relative">
      <PDFTitle title={title} />

      <Card display="flex">
        <Box flex={1} position="relative" p="16" display="inline-block">
          <Text variant="cardTitle" mb="24" as="h2">
            Retirement Income
          </Text>

          <Box height={330}>
            <ColumnChart
              ref={chartRef}
              series={series}
              categories={categories}
              isStacked
              customSettings={customSettings}
              yAxisType="currency"
            />

            {!isPdf && (
              <Box position="absolute" top={8} right={8}>
                <IconButton
                  aria-label={selectedIndex === null ? 'Expand details' : 'Collapse details'}
                  icon={
                    selectedIndex === null
                      ? 'far fa-angle-double-left'
                      : 'far fa-angle-double-right'
                  }
                  variant="subtleInactive"
                  onClick={() => {
                    setSelectedIndex(selectedIndex === null ? 0 : null);
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Breakdown
          values={values}
          categories={categories}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      </Card>
    </Box>
  );
};
